import React from 'react';

import { Box } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { useAuth } from '@opendoor/auth-fe/context';
import { dashboardHref } from 'helpers/dashboardSession';

const DASHBOARD_HREF = dashboardHref('nav');
const DASHBOARD_LABEL = 'Dashboard';
const DASHBOARD_ARIA_LABEL = 'View Dashboard';

const isDashboardPath = (slug: string): boolean => {
  const normalizedSlug = slug.replace(/^\/+/, '');
  return normalizedSlug === 'dashboard' || normalizedSlug.startsWith('dashboard/');
};

interface DashboardNavItemProps {
  slug: string;
}

export const DashboardNavItem: React.FC<DashboardNavItemProps> = ({ slug }) => {
  const { authentication } = useAuth();
  const isDashboardActive = isDashboardPath(slug);

  if (authentication.state === 'authenticating') {
    return <DashboardNavSkeleton />;
  }

  if (authentication.state !== 'authenticated') {
    return null;
  }

  return (
    <Box
      role="listitem"
      className="pointer-events-none absolute left-full top-0 block translate-x-2 pr-8"
    >
      <Box
        href={DASHBOARD_HREF}
        aria-label={DASHBOARD_ARIA_LABEL}
        aria-current={isDashboardActive ? 'page' : undefined}
        analyticsName="cosmos-mega-nav-dashboard"
        className="pointer-events-auto inline-flex h-10 w-[108px] items-center justify-center rounded-xl px-3 py-2 font-medium text-od-content-primary no-underline hover:bg-od-bg-hover-primary"
      >
        <Text as="span" className="text-label-md text-inherit">
          {DASHBOARD_LABEL}
        </Text>
      </Box>
    </Box>
  );
};

const DashboardNavSkeleton: React.FC = () => (
  <Box
    role="listitem"
    aria-hidden
    className="pointer-events-none absolute left-full top-0 block translate-x-2 pr-8"
  >
    <Box
      data-dashboard-nav-skeleton
      className="flex h-10 w-[108px] items-center justify-center px-3 py-2"
    >
      <Box className="skeleton h-4 w-full rounded-md bg-od-bg-tertiary" />
    </Box>
  </Box>
);
